import { page } from '$app/stores';
import * as shopifyCart from '$lib/api/ecom/shopifyCart';
import { type Cart } from '$lib/types/ecom/apiTypes';
import { derived, get, writable } from 'svelte/store';
import api from '$lib/api/ecom/shopify';

const CART_ID_STORAGE_KEY = 'cart_id';

type OptimisticUpdate =
	| {
		type: 'remove';
		cartLineId: string;
	}
	| {
		type: 'quantity';
		cartLineId: string;
		quantity: number;
	}
	| {
		type: 'add';
		variantId: string;
		quantity: number;
		title?: string;
		imageUrl?: string;
		sellingPlanId?: string;
	};

export const customerToken = writable<string | undefined>(undefined);
export const customer = writable<any>(null);

export const cartOpen = writable<boolean>(false);
export const cart = writable<Cart | undefined>(undefined);

const updateQueue = writable<(() => Promise<Cart>)[]>([]);
const currentRequest = writable<(() => Promise<Cart>) | undefined>(undefined);

const optimisticUpdates = writable<Record<string, OptimisticUpdate>>({})

updateQueue.subscribe(async (requests) => {
	if (get(currentRequest)) return

	const request = requests[0]

	if (!request) return

	currentRequest.set(request)

	const newCart = await request()

	cart.set(newCart)

	currentRequest.set(undefined)

	updateQueue.update((val) => {
		const newQueue = val.filter((item) => item !== request)
		return newQueue
	})

})

function addNewRequestToQueue(request: () => Promise<Cart>) {
	updateQueue.update((val) => {
		val.push(request)
		return val
	})
}

export const optimisticCart = derived([cart, optimisticUpdates], ([$cart, $optimisticUpdates]) => {
	if (!$cart) return undefined;

	const newCart: Cart = JSON.parse(JSON.stringify($cart)) ?? [];
	if (!newCart.lines) newCart.lines = { edges: [] };

	Object.values($optimisticUpdates).forEach((update) => {
		if (update.type === 'remove') {
			if (!newCart.lines?.edges) return;

			newCart.lines.edges = newCart.lines?.edges?.filter(
				(item) => item?.node?.id !== update.cartLineId
			);
		} else if (update.type === 'quantity') {
			const item = newCart.lines?.edges?.find((item) => item?.node?.id === update.cartLineId);

			if (!item?.node) return;

			item.node.isOptimistic = true;
			item.node.quantity = update.quantity;
		} else if (update.type === 'add') {
			if (!newCart?.lines?.edges) return;

			const existingItem = newCart.lines.edges.find(
				(item) => item?.node?.merchandise?.id === update.variantId
			);
			if (existingItem?.node?.quantity) {
				existingItem.node.quantity += update.quantity;
				existingItem.node.isOptimistic = true;
				return;
			}

			newCart.lines.edges.push({
				node: {
					id: 'optimistic-' + update.variantId,
					isOptimistic: true,
					quantity: update.quantity,
					merchandise: {
						id: update.variantId,
						product: {
							title: update.title
						},
						image: {
							url: update.imageUrl,
							altText: update.title
						}
					}
				}
			});
		}
	});

	return newCart;
});

export const lastAddedItemId = writable<string | undefined>(undefined);

export const lastAddedItem = derived(
	[optimisticCart, lastAddedItemId],
	([$optimisticCart, $lastAddedItemId]) => {

		if (!$optimisticCart || !$lastAddedItemId) return undefined;
		const lastAddedItem = $optimisticCart.lines?.edges?.find(
			(item) => item?.node?.merchandise?.id === $lastAddedItemId
		);

		return lastAddedItem;
	}
);

function addOptimisticUpdate(update: OptimisticUpdate) {
	const id = Date.now().toString();
	optimisticUpdates.update((updates) => {
		updates[id] = update;
		return updates;
	});

	return id;
}

function cleanupOptimisticUpdate(id: string) {
	optimisticUpdates.update((updates) => {
		delete updates[id];
		return updates;
	});

	return id;
}

export const showLocationOptions = writable<boolean>(false);

export async function init() {
	await initCartFromLocalStorage();
	applyCustomerDiscountCodes()
}

function applyCustomerDiscountCodes() {
	const discountCode = get(page)?.data?.activeDiscount?.code

	if (discountCode) applyDiscountCodes([discountCode])
}

async function initCartFromLocalStorage() {
	const cookieCartId = localStorage.getItem(CART_ID_STORAGE_KEY);
	if (!cookieCartId) return;

	let newCart = await loadCart(cookieCartId);

	if (newCart?.buyerIdentity?.customer?.id && !get(page).data.customerToken) {
		// init a new cart on logout, since there is no way to remove a customer after it has been added, and therefore the user would still be signed in when clicking on checkout
		newCart = await createCart()
	} else {
		newCart = await shopifyCart.updateBuyerIdentity(cookieCartId, get(page).data.customerToken, get(page).data.location);
	}

	//Filter wholesale / consumer products
	let productsDeleted = false
	for (const item of newCart?.lines?.edges ?? []) {
		const type = item?.node?.merchandise?.product?.product_type?.value
		if ((get(page).data.customerType || type === 'wholsale') && type !== get(page).data.customerType) {
			await removeFromCart(item?.node?.id)
			productsDeleted = true
		}
	}

	if (productsDeleted) {
		// Only reload cart again if products were deleted
		newCart = await loadCart(cookieCartId);
	}

	cart.set(newCart);

	return newCart;
}

function writeCartIdToLocalStorage(cartId: string) {
	if (typeof localStorage === 'undefined') return;
	localStorage.setItem(CART_ID_STORAGE_KEY, cartId);
}

export function openCart() {
	cartOpen.set(true);
}

export function closeCart() {
	cartOpen.set(false);
}

async function loadCart(cartId: string) {
	const newCart = await shopifyCart.load(cartId);
	cart.set(newCart);
	return newCart;
}

async function createCart() {
	const newCart = await shopifyCart.create(get(page).data.customerToken, get(page).data.location);
	cart.set(newCart);
	return newCart;
}

async function getCartId() {
	const cartId = get(cart)?.id ?? (await initCartFromLocalStorage())?.id ?? (await createCart()).id;

	if (!cartId) throw new Error('Failed to init cart');

	return cartId;
}

export async function updateBuyerIdentity(customerAccessToken: string | null, countryCode: string) {
	const cartId = await getCartId();
	const newCart = await shopifyCart.updateBuyerIdentity(cartId, customerAccessToken, countryCode);
	cart.set(newCart);
	return newCart;
}

export async function updateCartItemQuantity(
	cartLineId: string | undefined,
	quantity: number | undefined
) {
	if (!cartLineId || (!quantity && quantity !== 0)) return;

	const optimisticUpdateId = addOptimisticUpdate({ type: 'quantity', cartLineId, quantity });

	addNewRequestToQueue(async () => {
		try {
			const cartId = await getCartId();
			return await shopifyCart.updateQuantity(cartId, cartLineId, quantity);
		} finally {
			cleanupOptimisticUpdate(optimisticUpdateId);
		}
	})
}

export async function applyDiscountCodes(codes: string[] = []) {
	addNewRequestToQueue(async () => {
		const cartId = await getCartId();
		return await shopifyCart.applyDiscountCodes(cartId, codes);
	})
}

export async function removeFromCart(cartLineId: string | undefined) {
	if (!cartLineId) return;

	const optimisticUpdateId = addOptimisticUpdate({ type: 'remove', cartLineId });

	addNewRequestToQueue(async () => {
		try {
			const cartId = await getCartId();
			return await shopifyCart.removeItem(cartId, cartLineId);
		} finally {
			cleanupOptimisticUpdate(optimisticUpdateId);
		}
	})
}

export async function addToCart(
	variantId: string | undefined,
	quantity: number | undefined,
	price: number,
	currency: string,
	title?: string,
	imageUrl?: string,
	sellingPlanId?: string
) {

	if (!variantId || (!quantity && quantity !== 0)) return;

	try {
		window.gtag('event', 'add_to_cart', {
			currency: currency,
			value: price * quantity,
			items: [
				{
					item_id: variantId,
					item_name: title,
					price: price,
					quantity: quantity
				}
			]
		});
	} catch (err) { }

	try {
		fbq('track', 'AddToCart', {
			content_name: title,
			content_type: 'product',
			value: price * quantity,
			currency: currency
		});
	} catch (err) { }

	const optimisticUpdateId = addOptimisticUpdate({
		type: 'add',
		variantId,
		quantity,
		title,
		imageUrl,
		sellingPlanId
	});

	openCart();

	addNewRequestToQueue(async () => {
		try {
			const cartId = await getCartId();
			const newCart = await shopifyCart.addItem(cartId, variantId, quantity, sellingPlanId);
			cart.set(newCart);
			lastAddedItemId.set(variantId);
			return newCart
		} finally {
			cleanupOptimisticUpdate(optimisticUpdateId);
		}
	})
}

cart.subscribe((cart) => {
	if (!cart?.id) return;
	writeCartIdToLocalStorage(cart?.id);
});

export async function checkout() {
	// Update buyers identity
	await updateBuyerIdentity(get(page).data.customerToken, get(page).data.location);

	const url = get(cart)?.checkoutUrl;

	if (!url) return;

	// Looks like we now get the correct domain from the new cart api
	/*
	const baseUrl = get(page).data.pageConfig?.shopify_checkout_base_url;
	if (baseUrl) {
		url = url.split('.myshopify.com')[1];
		url = 'https://' + baseUrl + url;
	}
	*/

	window.location.href = url;
}

export async function quickCheckout(variantId: string, quantity: number) {
	addToCart(variantId, quantity);
	await checkout();
}

export const cartChanged = writable<boolean>(false);
let cartChangeTimeout: any;

optimisticCart.subscribe(() => {
	cartChanged.set(true);
	const timeout = (cartChangeTimeout = setTimeout(() => {
		if (timeout !== cartChangeTimeout) return;
		cartChanged.set(false);
	}, 500));
});

export async function getSubscriptionData(location: string, lang: string, ruleName: string = 'Coffee Subscription') {
	const res = await fetch('/api/get-selling-plans');
	const subscriptionData = await res.json();

	const productIds: any[] = [];

	if (subscriptionData?.subscriptions_rules?.length === 0) {
		return;
	}

	let selling_plans = []

	for (const rule of subscriptionData.subscription_rules ?? []) {
		if (ruleName === rule.name) {
			selling_plans = rule.selling_plans
			for (const product of rule.product_variants ?? []) {
				const id = 'gid://shopify/Product/' + product.product_id;
				if (!productIds.includes(id)) {
					productIds.push(id);
				}
			}
		}
	}

	const productData = await api.getProductsById(productIds, location, lang);

	return {
		sellingPlans: selling_plans,
		subscriptionProducts: productData?.nodes ?? []
	};
}